import React, { useMemo, useRef, useState } from 'react';
import moment from 'moment-timezone';
import {
  AsyncConnector,
  Button,
  DataGrid,
  DataGridHeader,
  Flashy,
  Dropdown,
  Form,
  Grid,
  GridColumn,
  GridRow,
  Layout,
  LayoutItem,
  PluralizeUtil,
  Suspense,
  useApi,
} from '@axiom/ui';
import { InterviewOwner, InterviewsStatus, InterviewSort } from '@axiom/const';
import type { UserInterview } from '@axiom/validation';

import { UserApi } from '../../api/user-api';
import type {
  ReadUserInterviewsBody,
  ReadUserInterviewsType,
} from '../../api/user-activity-api';
import { UserActivityApi } from '../../api/user-activity-api';
import { useInterval } from '../../hooks/useInterval';
import {
  CalendarScheduleModes,
  TalentInterviewAvailabilityModal,
} from '../TalentInterviewAvailabilityModal/TalentInterviewAvailabilityModal';
import { useInterviewAvailabilityCandidateData } from '../../hooks/useInterviewAvailabilityCandidateData';
import { CalendarApi } from '../../api/calendar-api';
import type { WorkspaceInterviewsType } from '../../schema/workspace-interviews-form-schema';
import { WorkspaceInterviewsFormSchema } from '../../schema/workspace-interviews-form-schema';
import { InterviewGridCellsUtil } from '../../utils/InterviewGridCellsUtil';
import {
  InterviewGridCancelModal,
  type CancelEventSubmitProps,
} from '../InterviewGridCancelModal/InterviewGridCancelModal';
import { CalendarConversationsApi } from '../../api/calendar-conversations-api';
import { InterviewAttendeesAndLinkModalEdit } from '../InterviewAttendeesAndLinkModalEdit/InterviewAttendeesAndLinkModalEdit';

const ownerFilterOptions = Object.entries(InterviewOwner).map(
  ([key, value]) => ({
    id: key,
    value,
  })
);

const statusFilterOptions = [
  { id: 'all', value: 'All' },
  ...Object.values(InterviewsStatus)
    .sort()
    .map(item => {
      return {
        id: item,
        value: item,
      };
    }),
];

const getInterviewStatusFilter = (
  status: WorkspaceInterviewsType['FILTER_BY_STATUS']
) => {
  return status === 'all'
    ? null
    : (status as ReadUserInterviewsBody['filters']['interviewStatus']);
};

const parseSortParams = (sort: Record<string, string | null | undefined>) => {
  const [[sortField, sortOrder] = [null, null]] = Object.entries(sort);
  const validOrder: 'asc' | 'desc' | null =
    sortOrder === 'asc' || sortOrder === 'desc' ? sortOrder : null;
  return {
    field:
      validOrder === null
        ? InterviewSort.INTERVIEW_STATUS
        : sortField || InterviewSort.INTERVIEW_STATUS,
    order: validOrder ?? 'asc',
  };
};

export const WorkspaceInterviews = () => {
  const [{ data: user }] = useApi(UserApi.readSessionUser());
  const [resultCount, setResultCount] = useState(0);
  const [activeInterviewData, setActiveInterviewData] =
    useState<UserInterview | null>(null);
  const [deleteEvent, setDeleteEvent] = useState<UserInterview | null>(null);
  const [editAttendeesData, setEditAttendeesData] =
    useState<UserInterview | null>(null);
  const [editInterviewTimeData, setEditInterviewTimeData] =
    useState<UserInterview | null>(null);
  const { refreshInterval, stateChangeTrigger } = useInterval(5 * 60 * 1000); // 5min

  const [filterByOwner, setFilterByOwner] = useState<
    WorkspaceInterviewsType['FILTER_BY_OWNER']
  >('accountManagerOwnerOrSalesLead');
  const [filterByStatus, setFilterByStatus] =
    useState<WorkspaceInterviewsType['FILTER_BY_STATUS']>('all');
  const activeCandidateSource = activeInterviewData ?? editInterviewTimeData;
  const { interviewCandidate } = useInterviewAvailabilityCandidateData({
    candidateInterview: activeCandidateSource?.candidateOpportunity.candidate,
  });

  const GridConnector = useMemo(() => {
    return new AsyncConnector({
      pageSize: 20,
      getData: ({ sort, page }) => {
        const { field, order } = parseSortParams(sort);

        const body: ReadUserInterviewsBody = {
          filters: {
            assigneeType:
              filterByOwner as ReadUserInterviewsBody['filters']['assigneeType'],
            interviewStatus: getInterviewStatusFilter(filterByStatus),
          },
          sort: field,
          order,
          page: page || 1,
          pageSize: 20,
          timezone: moment.tz.guess(),
        };

        return UserActivityApi.readUserInterviews({ userId: user.id, body });
      },
      parseData: ({ meta, data }) => {
        setResultCount(meta.resultCount);

        return {
          currentPage: meta.currentPage,
          totalPages: meta.pageCount,
          data,
        };
      },
    });
  }, [user, stateChangeTrigger, filterByOwner, filterByStatus]);

  const runDelay = async () => {
    return new Promise<void>(resolve => {
      setTimeout(resolve, 1500);
    });
  };

  const handleOpenInterviewModal = (data: UserInterview) => {
    setActiveInterviewData(data);
  };
  const handleCloseInterviewModal = async (addDelay = false) => {
    setActiveInterviewData(null);

    /**
     * Due to the submitting of the calendar data is within the modal
     * we only have access to the close function. So we delay here
     * before refreshing the data on the grid
     * */
    if (addDelay) {
      await runDelay();
      refreshInterval();
    }
  };

  const handleOpenInterviewGridCancelModal = (data: UserInterview) => {
    setDeleteEvent(data);
  };

  const handleDeleteSubmit = async (data: CancelEventSubmitProps) => {
    await CalendarApi.deleteCalendarEvents(data.calendarId, [data.eventId]);

    await runDelay();

    if (data.calendarId) {
      await Promise.all([
        CalendarApi.refreshCalendar(data.calendarId),
        CalendarApi.refreshCalendarEvents(data.calendarId),
      ]);
    }

    refreshInterval();
    setDeleteEvent(null);
  };

  // ag-grid context menu items can fire their action twice per click (the
  // hook attaches its own click handler to the same element our inline
  // onClick is on, so stopPropagation doesn't help). Guard the dismiss
  // handler with an in-flight ref so a single user click only produces one
  // DELETE request.
  const dismissInFlightRef = useRef(false);
  const handleDismissRequest = async (interview: UserInterview) => {
    if (dismissInFlightRef.current) return;
    if (!interview.clientRequest?.id) return;
    dismissInFlightRef.current = true;
    try {
      await CalendarConversationsApi.deleteInterviewRequest(
        interview.clientRequest.id
      );
      await runDelay();
      refreshInterval();
    } finally {
      dismissInFlightRef.current = false;
    }
  };

  return (
    <>
      <Layout name="WORKSPACE_INTERVIEWS" direction="vertical" stretched>
        <LayoutItem verticalGutter="16px">
          <Form
            submitOnChange
            onSubmit={changedData => {
              if (changedData.FILTER_BY_OWNER !== undefined) {
                setFilterByOwner(
                  changedData.FILTER_BY_OWNER === null
                    ? 'accountManagerOwnerOrSalesLead'
                    : changedData.FILTER_BY_OWNER
                );
              }
              if (changedData.FILTER_BY_STATUS !== undefined) {
                setFilterByStatus(
                  changedData.FILTER_BY_STATUS === null
                    ? 'all'
                    : changedData.FILTER_BY_STATUS
                );
              }
            }}
            name="INTERVIEW_GRID_FORM"
            schema={WorkspaceInterviewsFormSchema}
            initialValues={{
              FILTER_BY_OWNER: filterByOwner,
              FILTER_BY_STATUS: filterByStatus,
            }}
          >
            {() => {
              return (
                <Grid name="FILTERS_REFRESH">
                  <GridRow stretched>
                    <GridColumn width={2}>
                      <Dropdown
                        name="FILTER_BY_OWNER"
                        displayKey="value"
                        valueKey="id"
                        options={ownerFilterOptions}
                        label="Filter by Owner"
                        placeholder="Select an owner"
                      />
                    </GridColumn>
                    <GridColumn width={2}>
                      <Dropdown
                        name="FILTER_BY_STATUS"
                        displayKey="value"
                        valueKey="id"
                        options={statusFilterOptions}
                        label="Filter by Status"
                        placeholder="Select a status"
                      />
                    </GridColumn>
                    <GridColumn
                      width={8}
                      textAlign="right"
                      verticalAlign="bottom"
                    >
                      <Layout position="right">
                        <LayoutItem>
                          <Button
                            name="REFRESH_BUTTON"
                            pattern="primary"
                            variation="outline"
                            onClick={() => {
                              refreshInterval();
                            }}
                          >
                            Refresh
                          </Button>
                        </LayoutItem>
                      </Layout>
                    </GridColumn>
                  </GridRow>
                </Grid>
              );
            }}
          </Form>
        </LayoutItem>
        <LayoutItem name="RESULT_COUNT_DISPLAY" bottomGutter="8px">
          <Flashy bold>
            {resultCount}{' '}
            {PluralizeUtil({ text: 'Interview', count: resultCount })}
          </Flashy>
        </LayoutItem>
        <LayoutItem stretched>
          <DataGrid<ReadUserInterviewsType>
            name="INTERVIEWS_GRID"
            displayMode="infinite"
            dataSource={GridConnector}
            noResultsMessage="Interviews that are assigned to you will appear here"
            getContextMenuItems={menuData => {
              return InterviewGridCellsUtil.getContextMenuItems({
                params: menuData,
                openScheduleInterviewModal: handleOpenInterviewModal,
                openEditAttendeesModal: setEditAttendeesData,
                openEditInterviewTimeModal: setEditInterviewTimeData,
                openInterviewGridCancelModal:
                  handleOpenInterviewGridCancelModal,
                onDismissRequest: (interview: UserInterview) => {
                  void handleDismissRequest(interview);
                },
              });
            }}
            allowContextMenu
            simple
          >
            <DataGridHeader<UserInterview>
              name={InterviewSort.INTERVIEW_STATUS}
              displayName="Status"
              sortingOrder={['asc', 'desc']}
              initialSort="asc"
              width={152}
              cellRender={InterviewGridCellsUtil.status}
            />
            <DataGridHeader<UserInterview>
              name={InterviewSort.TALENT_NAME}
              displayName="Talent"
              sortingOrder={['asc', 'desc', null]}
              width={160}
              cellRender={InterviewGridCellsUtil.talent}
            />
            <DataGridHeader<UserInterview>
              name={InterviewSort.JOB_NAME}
              displayName="Opportunity"
              sortingOrder={['asc', 'desc', null]}
              width={280}
              cellRender={InterviewGridCellsUtil.opportunity}
            />
            <DataGridHeader<UserInterview>
              name={InterviewSort.INTERVIEW_DATE}
              displayName="Interview date & round"
              sortingOrder={['asc', 'desc', null]}
              width={312}
              cellRender={props =>
                InterviewGridCellsUtil.dateAndRound(props, {
                  onClick: handleOpenInterviewModal,
                })
              }
            />
            <DataGridHeader<UserInterview>
              name="attendees"
              displayName="Confirmed attendees"
              width={196}
              cellRender={InterviewGridCellsUtil.attendees}
            />
            <DataGridHeader<UserInterview>
              name="meeting_link"
              displayName="Meeting link"
              width={144}
              cellRender={props =>
                InterviewGridCellsUtil.meetingLink(props, {
                  onAddLink: setEditAttendeesData,
                })
              }
            />
            <DataGridHeader<UserInterview>
              name="contextMenu"
              pinned="right"
              lockPinned
              displayName=""
              width={64}
              cellRender={InterviewGridCellsUtil.contextMenu}
            />
          </DataGrid>
        </LayoutItem>
      </Layout>

      {activeInterviewData && interviewCandidate ? (
        <Suspense>
          <TalentInterviewAvailabilityModal
            name="INTERVIEW_AUTO_MODAL"
            user={user}
            candidate={interviewCandidate}
            onClose={addDelay => {
              void handleCloseInterviewModal(addDelay);
            }}
            clientRequestId={activeInterviewData.clientRequest?.id}
            opportunityId={activeInterviewData.opportunityId}
            opportunityCandidateStatus={
              activeInterviewData.candidateOpportunity.candidateStatus
            }
            defaultMode={CalendarScheduleModes.INTERVIEW}
          />
        </Suspense>
      ) : null}
      {deleteEvent ? (
        <Suspense>
          <InterviewGridCancelModal
            name="DELETE_INTERVIEW_MODAL"
            data={deleteEvent}
            onClose={() => {
              setDeleteEvent(null);
            }}
            onSchedule={handleOpenInterviewModal}
            onSubmit={async data => {
              await handleDeleteSubmit(data);
            }}
          />
        </Suspense>
      ) : null}
      {editAttendeesData ? (
        <Suspense>
          <InterviewAttendeesAndLinkModalEdit
            interview={editAttendeesData}
            onClose={() => {
              setEditAttendeesData(null);
            }}
            onSave={async () => {
              await runDelay();
              refreshInterval();
            }}
          />
        </Suspense>
      ) : null}
      {editInterviewTimeData && interviewCandidate ? (
        <Suspense>
          <TalentInterviewAvailabilityModal
            name="EDIT_INTERVIEW_TIME_MODAL"
            user={user}
            candidate={interviewCandidate}
            editableEventId={
              editInterviewTimeData.calendarConversation?.invitees.find(
                i => i.candidate
              )?.id ?? null
            }
            editableConversationId={
              editInterviewTimeData.calendarConversation?.id ?? null
            }
            onClose={addDelay => {
              void handleCloseInterviewModal(addDelay);
              setEditInterviewTimeData(null);
            }}
            opportunityId={editInterviewTimeData.opportunityId}
            opportunityCandidateStatus={
              editInterviewTimeData.candidateOpportunity.candidateStatus
            }
            defaultMode={CalendarScheduleModes.INTERVIEW}
          />
        </Suspense>
      ) : null}
    </>
  );
};
